import { useState, useMemo, useRef, useEffect } from "react";
import { PANEL_EVENTS, PANEL_SCENARIOS } from "@/data/panelScenarios";
import { useFacuAudio } from "@/hooks/useFacuAudio";
import { Bell, BellOff, RotateCcw, Wrench, Zap, Activity, AlertTriangle, ChevronRight, Play, RefreshCw, Power, Volume2, VolumeX } from "lucide-react";

// FACU state model:
// events: [{ id, eventKey, state, addr, desc, ts, acked }]
// nacActive: bool (notification appliances on)
// piezoActive: bool (panel buzzer on)
// silenced: bool
// drillActive: bool
// testMode: bool

const initialState = {
  events: [],
  nacActive: false,
  piezoActive: false,
  silenced: false,
  drillActive: false,
  testMode: false,
  acPower: true,
  cycleIdx: 0,
  log: [],
};

function reducer(state, action) {
  const now = new Date().toISOString().slice(11, 19);
  switch (action.type) {
    case "INJECT": {
      const ev = PANEL_EVENTS[action.event];
      if (!ev) return state;
      const newEvent = { id: `${action.event}-${Date.now()}`, eventKey: action.event, ...ev, ts: now, acked: false };
      const events = [...state.events, newEvent];
      const isAlarm = ev.state === "alarm";
      return {
        ...state,
        events,
        piezoActive: true,
        nacActive: state.nacActive || (isAlarm && !state.testMode),
        acPower: action.event === "ac-loss" ? false : state.acPower,
        log: [...state.log, { ts: now, msg: `EVENT: ${ev.label} (${ev.addr})` }],
      };
    }
    case "ACK": {
      if (state.events.length === 0) return state;
      const events = state.events.map((e) => ({ ...e, acked: true }));
      return { ...state, events, piezoActive: false, log: [...state.log, { ts: now, msg: "ACKNOWLEDGED — piezo silenced, condition latched." }] };
    }
    case "SILENCE": {
      if (!state.nacActive && !state.drillActive) return state;
      return { ...state, nacActive: false, silenced: true, log: [...state.log, { ts: now, msg: "SILENCE — NACs OFF (alarm condition still active)." }] };
    }
    case "RESET": {
      return {
        ...initialState,
        log: [...state.log, { ts: now, msg: "RESET — system returned to normal." }],
      };
    }
    case "DRILL": {
      return {
        ...state,
        drillActive: true,
        nacActive: true,
        events: [{ id: `drill-${Date.now()}`, eventKey: "drill-test", ...PANEL_EVENTS["drill-test"], ts: now, acked: false }],
        log: [...state.log, { ts: now, msg: "DRILL — manual fire drill activated; logged as DRILL not ALARM." }],
      };
    }
    case "TEST_TOGGLE": {
      return { ...state, testMode: !state.testMode, log: [...state.log, { ts: now, msg: `TEST mode ${!state.testMode ? "ON" : "OFF"} — NAC + central-station bypassed for test zone.` }] };
    }
    case "CYCLE": {
      if (state.events.length === 0) return state;
      return { ...state, cycleIdx: (state.cycleIdx + 1) % state.events.length };
    }
    case "RESTORE_AC": {
      return { ...state, acPower: true, log: [...state.log, { ts: now, msg: "AC primary power restored." }] };
    }
    default:
      return state;
  }
}

// Highest-priority active event for display: alarm > supervisory > trouble
function priorityEvent(events, cycleIdx) {
  if (events.length === 0) return null;
  const order = { alarm: 0, supervisory: 1, trouble: 2 };
  const sorted = [...events].sort((a, b) => order[a.state] - order[b.state]);
  return sorted[cycleIdx % sorted.length];
}

function useReducer2(initial, fn) {
  const [s, set] = useState(initial);
  const dispatch = (a) => set((cur) => fn(cur, a));
  return [s, dispatch];
}

export const PanelSimulator = ({ initialScenarioId = null, onScenarioComplete }) => {
  const [state, dispatch] = useReducer2(initialState, reducer);
  const [activeScenarioId, setActiveScenarioId] = useState(initialScenarioId);
  const [stepIdx, setStepIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [muted, setMuted] = useState(false);
  const logRef = useRef(null);
  const audio = useFacuAudio({ muted });

  const scenario = useMemo(
    () => PANEL_SCENARIOS.find((s) => s.id === activeScenarioId),
    [activeScenarioId]
  );
  const currentStep = scenario?.steps[stepIdx];

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [state.log]);

  // Auto-advance trigger / observe steps
  useEffect(() => {
    if (!currentStep) return;
    if (currentStep.kind === "trigger") {
      const t = setTimeout(() => {
        dispatch({ type: "INJECT", event: currentStep.event });
        setStepIdx((i) => i + 1);
      }, 700);
      return () => clearTimeout(t);
    }
  }, [currentStep, stepIdx, activeScenarioId]); // eslint-disable-line

  const evt = priorityEvent(state.events, state.cycleIdx);
  const hasAlarm = state.events.some((e) => e.state === "alarm");
  const hasSupervisory = state.events.some((e) => e.state === "supervisory");
  const hasTrouble = state.events.some((e) => e.state === "trouble") || !state.acPower;

  // Drive audio off the panel state. Sounds layer (alarm + trouble + piezo can run together).
  // In TEST mode NACs are bypassed — alarm sound suppressed.
  useEffect(() => {
    const alarmSounding = (hasAlarm || state.drillActive) && state.nacActive && !state.testMode;
    if (alarmSounding) audio.startAlarm(); else audio.stopAlarm();
  }, [hasAlarm, state.drillActive, state.nacActive, state.testMode]); // eslint-disable-line

  useEffect(() => {
    if (hasTrouble) audio.startTrouble(); else audio.stopTrouble();
  }, [hasTrouble]); // eslint-disable-line

  useEffect(() => {
    if (hasSupervisory) audio.startSupervisory(); else audio.stopSupervisory();
  }, [hasSupervisory]); // eslint-disable-line

  useEffect(() => {
    if (state.piezoActive) audio.startPiezo(); else audio.stopPiezo();
  }, [state.piezoActive]); // eslint-disable-line

  // Track scenario progression based on actions taken
  const performAction = (verb) => {
    audio.click();
    if (verb === "ack") dispatch({ type: "ACK" });
    if (verb === "silence") dispatch({ type: "SILENCE" });
    if (verb === "reset") dispatch({ type: "RESET" });
    if (verb === "drill") dispatch({ type: "DRILL" });
    if (verb === "test") dispatch({ type: "TEST_TOGGLE" });

    if (!scenario || !currentStep || currentStep.kind !== "action") return;
    if (currentStep.verb === verb) {
      // Advance immediately to avoid race conditions with rapid clicks.
      setStepIdx((i) => i + 1);
      setFeedback({ ok: true, msg: "Correct." });
      setTimeout(() => setFeedback(null), 1000);
    } else {
      setFeedback({ ok: false, msg: `Expected ${currentStep.verb.toUpperCase()} first.` });
      setTimeout(() => setFeedback(null), 1500);
    }
  };

  const advanceObserve = () => {
    if (currentStep?.kind === "observe") setStepIdx((i) => i + 1);
  };

  const startScenario = (id) => {
    dispatch({ type: "RESET" });
    setActiveScenarioId(id);
    setStepIdx(0);
    setFeedback(null);
  };

  const exitScenario = () => {
    setActiveScenarioId(null);
    setStepIdx(0);
    dispatch({ type: "RESET" });
    if (onScenarioComplete) onScenarioComplete();
  };

  const completed = scenario && stepIdx >= scenario.steps.length;

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="panel-simulator">
      {/* ─── PANEL FACE ─────────────────────────────────── */}
      <div className="lg:col-span-2">
        <div className="bg-zinc-950 border-4 border-zinc-800 p-5 sm:p-7 shadow-2xl" data-testid="panel-face">
          {/* Header / brand strip */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-mono tracking-[0.25em] text-zinc-500 uppercase">Generic Addressable FACU</div>
              <div className="text-zinc-100 font-heading text-xl font-bold tracking-tight">APM-7200</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { audio.ensureCtx(); setMuted((m) => !m); }}
                className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 border transition-colors ${
                  muted ? "border-zinc-700 text-zinc-500 hover:text-zinc-200" : "border-red-600 text-red-400 hover:text-red-300"
                }`}
                title={muted ? "Sound off — click to enable" : "Sound on — click to mute"}
                data-testid="panel-mute-toggle"
              >
                {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                {muted ? "MUTED" : "SOUND"}
              </button>
              <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                <Power className={`w-3 h-3 ${state.acPower ? "text-green-400" : "text-zinc-600"}`} />
                <span>{state.acPower ? "AC OK" : "BATT"}</span>
              </div>
            </div>
          </div>

          {/* LED bank */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <Led on={hasAlarm} color="red" label="ALARM" />
            <Led on={hasSupervisory} color="yellow" label="SUPV" />
            <Led on={hasTrouble} color="amber" label="TRBL" />
            <Led on={state.silenced} color="blue" label="SILENCED" />
            <Led on={state.acPower} color="green" label="AC PWR" />
            <Led on={!state.acPower} color="amber" label="BATT" />
            <Led on={state.testMode} color="blue" label="TEST" />
            <Led on={state.drillActive} color="purple" label="DRILL" />
          </div>

          {/* LCD display */}
          <div className="bg-emerald-950 border-2 border-emerald-900 p-4 font-mono text-emerald-300 text-sm mb-4 min-h-[120px]" data-testid="panel-lcd">
            {evt ? (
              <>
                <div className="text-[10px] text-emerald-500 mb-1">{evt.ts} · {state.events.length} ACTIVE EVENT(S){state.events.length > 1 ? ` · CYCLE ${state.cycleIdx + 1}/${state.events.length}` : ""}</div>
                <div className={`text-base font-bold tracking-wide ${evt.state === "alarm" ? "text-red-400" : evt.state === "supervisory" ? "text-yellow-300" : "text-amber-300"}`} data-testid="panel-lcd-state">
                  *{evt.state.toUpperCase()}*
                </div>
                <div className="mt-1 text-emerald-200">{evt.desc}</div>
                <div className="text-[10px] text-emerald-500 mt-1">ADDR {evt.addr} {evt.acked ? "· ACKED" : ""}</div>
              </>
            ) : (
              <div>
                <div className="text-[10px] text-emerald-500 mb-1">{new Date().toISOString().slice(11, 19)} · NORMAL</div>
                <div className="text-base font-bold">SYSTEM NORMAL</div>
                <div className="mt-1 text-emerald-500 text-xs">No active events. All zones supervised.</div>
              </div>
            )}
          </div>

          {/* NAC / Piezo indicators */}
          <div className="flex items-center gap-3 mb-4 text-xs font-mono">
            <span className={`px-2 py-1 ${state.nacActive ? "bg-red-600 text-white animate-pulse" : "bg-zinc-800 text-zinc-500"}`} data-testid="panel-nac-state">
              NAC {state.nacActive ? "ON" : "OFF"}
            </span>
            <span className={`px-2 py-1 ${state.piezoActive ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-500"}`}>
              PIEZO {state.piezoActive ? "ON" : "OFF"}
            </span>
            {state.events.length > 1 && (
              <button onClick={() => { audio.click(); dispatch({ type: "CYCLE" }); }} className="ml-auto px-2 py-1 bg-zinc-800 text-zinc-200 hover:bg-zinc-700" data-testid="panel-cycle-btn">
                CYCLE EVENTS →
              </button>
            )}
          </div>

          {/* Buttons row */}
          <div className="grid grid-cols-5 gap-2">
            <PanelBtn icon={<Bell className="w-4 h-4" />} label="ACK" tone="amber" onClick={() => performAction("ack")} testid="panel-btn-ack" />
            <PanelBtn icon={<BellOff className="w-4 h-4" />} label="SILENCE" tone="blue" onClick={() => performAction("silence")} testid="panel-btn-silence" />
            <PanelBtn icon={<RotateCcw className="w-4 h-4" />} label="RESET" tone="red" onClick={() => performAction("reset")} testid="panel-btn-reset" />
            <PanelBtn icon={<Activity className="w-4 h-4" />} label="DRILL" tone="purple" onClick={() => performAction("drill")} testid="panel-btn-drill" />
            <PanelBtn icon={<Wrench className="w-4 h-4" />} label="TEST" tone="zinc" onClick={() => performAction("test")} testid="panel-btn-test" />
          </div>
        </div>

        {/* Event log */}
        <div className="mt-4 card-tech p-4">
          <div className="overline mb-2"><span className="red-bar" />Event Log</div>
          <div ref={logRef} className="font-mono text-[11px] text-zinc-700 space-y-1 max-h-48 overflow-y-auto" data-testid="panel-event-log">
            {state.log.length === 0 ? <div className="text-zinc-400">— no events yet —</div> : state.log.map((l, i) => (
              <div key={`log-${l.ts}-${i}`}><span className="text-zinc-400">{l.ts}</span> &nbsp; {l.msg}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── SIDE PANEL: scenario or free-play ─────────── */}
      <div className="space-y-4">
        {scenario ? (
          <div className="card-tech p-5" data-testid="panel-scenario-panel">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="overline mb-1"><span className="red-bar" />Scenario · L{scenario.level}</div>
                <h3 className="font-heading text-lg font-bold tracking-tight">{scenario.title}</h3>
              </div>
              <button onClick={exitScenario} className="text-xs text-zinc-500 hover:text-red-600 font-mono uppercase" data-testid="panel-exit-scenario">Exit</button>
            </div>
            <p className="text-xs text-zinc-600 mb-3 leading-relaxed">{scenario.blurb}</p>

            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Step {Math.min(stepIdx + 1, scenario.steps.length)} / {scenario.steps.length}</div>

            {!completed ? (
              <div className="border-2 border-red-200 bg-red-50/40 p-3" data-testid="panel-current-step">
                <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-1">{currentStep.kind}</div>
                <p className="text-sm text-zinc-800 leading-relaxed">{currentStep.prompt}</p>
                {currentStep.hint && <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">💡 {currentStep.hint}</p>}
                {currentStep.kind === "observe" && (
                  <button onClick={advanceObserve} className="btn-primary text-xs mt-3" data-testid="panel-step-continue">
                    Got it <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <div className="border-2 border-green-300 bg-green-50/40 p-4 text-center" data-testid="panel-scenario-complete">
                <div className="font-heading text-lg font-bold mb-1">Scenario complete.</div>
                <p className="text-xs text-zinc-600 mb-3">You executed the correct response sequence.</p>
                <div className="flex gap-2 justify-center">
                  <button onClick={() => startScenario(scenario.id)} className="btn-outline text-xs" data-testid="panel-replay-scenario">
                    <RefreshCw className="w-3.5 h-3.5" /> Replay
                  </button>
                  <button onClick={exitScenario} className="btn-primary text-xs" data-testid="panel-pick-another">
                    Pick another <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {feedback && (
              <div className={`mt-3 p-2 text-xs font-mono ${feedback.ok ? "bg-green-100 text-green-800 border border-green-300" : "bg-amber-100 text-amber-900 border border-amber-300"}`} data-testid="panel-feedback">
                {feedback.ok ? "✓ " : "× "}{feedback.msg}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-zinc-100 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Free-play injectors</div>
            <FreePlayPanel dispatch={dispatch} click={audio.click} />
          </div>
        ) : (
          <>
            <div className="card-tech p-5" data-testid="panel-scenarios-list">
              <div className="overline mb-3"><span className="red-bar" />Guided Scenarios</div>
              <div className="space-y-1.5">
                {PANEL_SCENARIOS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => startScenario(s.id)}
                    className="w-full text-left p-2.5 border border-zinc-200 hover:border-red-600 hover:bg-red-50/30 transition-colors group"
                    data-testid={`panel-scenario-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[10px] bg-zinc-950 text-white px-1.5 py-0.5 flex-shrink-0">L{s.level}</span>
                        <span className="text-sm font-semibold text-zinc-900 truncate">{s.title}</span>
                      </div>
                      <Play className="w-3.5 h-3.5 text-zinc-400 group-hover:text-red-600 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card-tech p-5">
              <div className="overline mb-2"><span className="red-bar" />Free-play</div>
              <p className="text-xs text-zinc-600 mb-3">Inject any event manually and respond using the panel buttons.</p>
              <FreePlayPanel dispatch={dispatch} click={audio.click} />
              {!state.acPower && (
                <button onClick={() => dispatch({ type: "RESTORE_AC" })} className="btn-outline text-xs mt-3 w-full justify-center" data-testid="panel-restore-ac">
                  <Zap className="w-3.5 h-3.5" /> Restore AC power
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Led = ({ on, color, label }) => {
  const colorMap = {
    red: on ? "bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.8)]" : "bg-red-950",
    yellow: on ? "bg-yellow-300 shadow-[0_0_12px_rgba(253,224,71,0.8)]" : "bg-yellow-900",
    amber: on ? "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]" : "bg-amber-950",
    blue: on ? "bg-blue-400 shadow-[0_0_12px_rgba(96,165,250,0.8)]" : "bg-blue-950",
    green: on ? "bg-green-400 shadow-[0_0_12px_rgba(74,222,128,0.8)]" : "bg-green-950",
    purple: on ? "bg-purple-400 shadow-[0_0_12px_rgba(192,132,252,0.8)]" : "bg-purple-950",
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${colorMap[color]} transition-all`} />
      <span className={`text-[10px] font-mono tracking-wider ${on ? "text-zinc-100" : "text-zinc-600"}`}>{label}</span>
    </div>
  );
};

const PanelBtn = ({ icon, label, tone, onClick, testid }) => {
  const tones = {
    amber: "bg-amber-500 hover:bg-amber-400 text-zinc-950",
    blue: "bg-blue-500 hover:bg-blue-400 text-white",
    red: "bg-red-600 hover:bg-red-500 text-white",
    purple: "bg-purple-600 hover:bg-purple-500 text-white",
    zinc: "bg-zinc-700 hover:bg-zinc-600 text-zinc-100",
  };
  return (
    <button
      onClick={onClick}
      className={`${tones[tone]} font-mono text-[10px] tracking-widest font-bold py-2.5 transition-colors flex flex-col items-center gap-1`}
      data-testid={testid}
    >
      {icon}
      {label}
    </button>
  );
};

const FreePlayPanel = ({ dispatch, click }) => (
  <div className="grid grid-cols-2 gap-1.5">
    {Object.entries(PANEL_EVENTS).map(([key, ev]) => (
      <button
        key={key}
        onClick={() => { if (click) click(); dispatch({ type: "INJECT", event: key }); }}
        className="text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 border border-zinc-200 hover:border-red-600 hover:bg-red-50 text-left transition-colors"
        data-testid={`panel-inject-${key}`}
      >
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        {key.replace("-", " ")}
      </button>
    ))}
  </div>
);

export default PanelSimulator;
